import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Mail } from 'lucide-react';
import { buildReviewerUrl } from '@rv/shared';
import { api, errorMessage } from '../../lib/api';
import { homeFor, useMe } from '../../lib/auth';
import { Button, ErrorState, Field, FullPageSpinner, Input, Notice } from '../../components/ui';
import { Brand } from '../../shells/AdminLayout';

function ProductIllustration() {
  // Pure CSS/SVG illustration of the product: a design with pinned feedback. No external images.
  return (
    <div className="relative mx-auto w-full max-w-[440px]" aria-hidden>
      <div className="rounded-2xl bg-white/[0.06] p-3 ring-1 ring-white/10 backdrop-blur">
        <div className="overflow-hidden rounded-xl bg-white shadow-pop">
          <div className="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
            <span className="size-2 rounded-full bg-rose-300" /><span className="size-2 rounded-full bg-amber-300" /><span className="size-2 rounded-full bg-emerald-300" />
            <span className="ml-2 h-4 flex-1 rounded bg-slate-100" />
          </div>
          <div className="relative space-y-3 p-4">
            <div className="flex items-center justify-between"><span className="h-2.5 w-16 rounded bg-slate-800" /><span className="flex gap-2"><span className="h-2 w-8 rounded bg-slate-200" /><span className="h-2 w-8 rounded bg-slate-200" /><span className="h-2 w-8 rounded bg-slate-200" /></span></div>
            <div className="rounded-lg bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 p-5">
              <span className="block h-3.5 w-40 rounded bg-slate-800/85" />
              <span className="mt-2 block h-2 w-52 rounded bg-slate-700/30" />
              <span className="mt-4 inline-block h-6 w-24 rounded-md bg-slate-900" />
            </div>
            <div className="grid grid-cols-3 gap-2">{[0, 1, 2].map((i) => <span key={i} className="h-12 rounded-md bg-slate-100" />)}</div>
            <span className="absolute top-[70px] left-[182px] flex size-7 items-center justify-center rounded-full rounded-bl-sm border-2 border-white bg-accent-600 text-[11px] font-semibold text-white shadow-raise">1</span>
            <span className="absolute top-[128px] left-[88px] flex size-7 items-center justify-center rounded-full rounded-bl-sm border-2 border-white bg-teal-600 text-[11px] font-semibold text-white shadow-raise">2</span>
          </div>
        </div>
      </div>
      <div className="absolute -right-4 bottom-10 w-56 rounded-xl bg-white p-3 shadow-pop ring-1 ring-black/5">
        <div className="flex items-center gap-2"><span className="size-6 rounded-full bg-accent-600" /><span className="text-[12px] font-semibold text-slate-800">Client · QA</span><span className="ml-auto rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">High</span></div>
        <p className="mt-2 text-[12px] leading-snug text-slate-600">Headline wraps on mobile — can it be one line?</p>
      </div>
      <div className="absolute top-8 -left-5 rounded-xl bg-white px-3 py-2 shadow-pop ring-1 ring-black/5">
        <p className="text-[11px] text-slate-500">Version</p>
        <p className="text-[13px] font-semibold text-slate-900">V3 · Approved ✓</p>
      </div>
    </div>
  );
}

function AuthCard({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="grid min-h-full bg-white lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Brand />
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[380px] animate-pop-in">
            <h1 className="text-[26px] leading-tight font-semibold tracking-[-0.025em] text-ink">{title}</h1>
            {subtitle && <p className="mt-2 text-[14px] leading-relaxed text-slate-500">{subtitle}</p>}
            <div className="mt-8">{children}</div>
          </div>
        </div>
        <p className="text-[12px] text-slate-400">Secure review portal · Your session stays on this site only.</p>
      </div>
      <div className="relative hidden overflow-hidden bg-[#0f1021] lg:flex lg:flex-col lg:justify-center lg:px-14">
        <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(60% 50% at 70% 20%, rgba(99,102,241,.35), transparent 70%), radial-gradient(40% 40% at 10% 90%, rgba(20,184,166,.18), transparent 70%)" }} />
        <div className="relative">
          <ProductIllustration />
          <div className="mx-auto mt-14 max-w-[440px]">
            <p className="text-[22px] leading-snug font-semibold tracking-[-0.02em] text-white">Pin feedback on the exact element. Approve the exact version.</p>
            <ul className="mt-5 space-y-2.5 text-[13.5px] text-slate-300">
              {["Review saved designs and live websites", "Every version kept — nothing silently changes", "Pick sections across options, approve with confidence"].map((t) => (
                <li key={t} className="flex items-center gap-2.5"><span className="flex size-5 items-center justify-center rounded-full bg-white/10 text-[11px] text-emerald-300">✓</span>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const fragToken = () => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';

export function LoginPage() {
  const me = useMe();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'password' | 'link'>('password');
  const [linkRes, setLinkRes] = useState<any>(null);
  if (me.isLoading) return <FullPageSpinner />;
  if (me.data) return <Navigate to={sp.get('next') ?? homeFor(me.data)} replace />;
  const next = sp.get('next');
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : undefined;
  if (mode === 'link')
    return (
      <AuthCard title="Sign in with a link" subtitle="No password needed. We'll email you a link that signs you in.">
        {linkRes ? (
          <div className="space-y-4">
            <Notice tone={linkRes.emailConfigured ? 'green' : 'amber'}>{linkRes.message}</Notice>
            <Button variant="ghost" className="w-full" onClick={() => setLinkRes(null)}>Use a different email</Button>
          </div>
        ) : (
          <form
            className="space-y-4"
            noValidate
            onSubmit={async (e) => {
              e.preventDefault();
              setErr(null);
              setBusy(true);
              try {
                setLinkRes(await api('/auth/magic-link', { body: { email, ...(safeNext ? { next: safeNext } : {}) } }));
              } catch (e2) {
                setErr(errorMessage(e2));
              } finally {
                setBusy(false);
              }
            }}
          >
            <Field label="Email" htmlFor="lemail">
              <Input id="lemail" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            {err && <p className="text-[13px] text-red-600" role="alert">{err}</p>}
            <Button type="submit" variant="primary" className="h-10 w-full" loading={busy} disabled={!email}>
              Email me a sign-in link
            </Button>
          </form>
        )}
        <p className="mt-4 text-center text-[13px]">
          <button type="button" className="text-accent-700 hover:underline" onClick={() => { setMode('password'); setErr(null); }}>
            Sign in with a password instead
          </button>
        </p>
      </AuthCard>
    );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api('/auth/login', { body: { email, password } });
      await qc.invalidateQueries({ queryKey: ['me'] });
      const fresh = await qc.fetchQuery({ queryKey: ['me'], queryFn: () => api('/auth/me') });
      const next = sp.get('next');
      nav(next && next.startsWith('/') && !next.startsWith('//') ? next : homeFor(fresh as any), { replace: true });
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard title="Sign in" subtitle="Review designs, leave feedback and approve.">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {err && <p className="text-[13px] text-red-600" role="alert">{err}</p>}
        <Button type="submit" variant="primary" className="h-10 w-full" loading={busy}>
          Sign in
        </Button>
        <p className="text-center text-[13px]">
          <Link to="/forgot-password" className="text-accent-700 hover:underline">
            Forgot password?
          </Link>
        </p>
        <div className="flex items-center gap-3 pt-1 text-[12px] text-slate-400" aria-hidden>
          <span className="h-px flex-1 bg-line" />or<span className="h-px flex-1 bg-line" />
        </div>
        <Button className="h-10 w-full" icon={<Mail className="size-4" />} onClick={() => { setMode('link'); setErr(null); }}>
          Email me a sign-in link
        </Button>
      </form>
    </AuthCard>
  );
}

/** Lands here from a sign-in link: /magic#token=… (the token never reaches server logs or link-preview bots). */
export function MagicLinkPage() {
  const [token] = useState(fragToken);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  // Single-use token: consume exactly once even when effects run twice (React StrictMode).
  const started = useRef(false);
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    history.replaceState(null, '', location.pathname);
    api('/auth/magic/consume', { body: { token } })
      .then(async (r) => {
        await qc.invalidateQueries({ queryKey: ['me'] });
        const next = typeof r.next === 'string' && r.next.startsWith('/') && !r.next.startsWith('//') ? r.next : r.user.globalRole === 'client' ? '/portal' : '/admin';
        nav(next, { replace: true });
      })
      .catch((e) => setErr(errorMessage(e)));
  }, [token]);
  if (!token) return <AuthCard title="Sign-in link missing"><Notice>Open the link from your email or message again.</Notice><p className="mt-4 text-center text-[13px]"><Link to="/login" className="text-accent-700 hover:underline">Go to sign in</Link></p></AuthCard>;
  if (err)
    return (
      <AuthCard title="This link can't be used">
        <Notice tone="amber">{err}</Notice>
        <p className="mt-4 text-center text-[13px]"><Link to="/login" className="text-accent-700 hover:underline">Go to sign in</Link></p>
      </AuthCard>
    );
  return <FullPageSpinner />;
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <AuthCard title="Reset your password">
      {res ? (
        <Notice tone={res.emailConfigured ? 'green' : 'amber'}>{res.message}</Notice>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setErr(null);
            try {
              setRes(await api('/auth/password/forgot', { body: { email } }));
            } catch (e2) {
              setErr(errorMessage(e2));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label="Email" htmlFor="femail">
            <Input id="femail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {err && <p className="text-[13px] text-red-600">{err}</p>}
          <Button type="submit" variant="primary" className="w-full" loading={busy}>
            Send reset link
          </Button>
        </form>
      )}
      <p className="mt-4 text-center text-[13px]">
        <Link to="/login" className="text-accent-700 hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const [token] = useState(fragToken);
  const nav = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Reset asked for on a reviewed website: offer the way back there (the address was validated by the server).
  const [doneBack, setDoneBack] = useState<string | null | undefined>(undefined);
  if (doneBack !== undefined)
    return (
      <AuthCard title="Password changed" subtitle="Use your new password next time you log in. Other sign-ins were signed out.">
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto size-12 text-emerald-500" aria-hidden />
          {doneBack ? (
            <a href={buildReviewerUrl(doneBack)} className="block"><Button variant="primary" className="w-full">Back to the website</Button></a>
          ) : (
            <Button variant="primary" className="w-full" onClick={() => nav('/login', { replace: true })}>Log in</Button>
          )}
        </div>
      </AuthCard>
    );
  if (!token) return <AuthCard title="Reset link missing"><Notice>Open the link from your email or from your administrator again.</Notice></AuthCard>;
  return (
    <AuthCard title="Choose a new password" subtitle="All your other sessions will be signed out.">
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (pw !== pw2) return setErr('Passwords do not match');
          setBusy(true);
          setErr(null);
          try {
            const r = await api('/auth/password/reset', { body: { token, password: pw } });
            history.replaceState(null, '', location.pathname);
            const back = typeof r.returnUrl === 'string' && /^https?:\/\//.test(r.returnUrl) ? r.returnUrl : null;
            setDoneBack(back);
          } catch (e2) {
            setErr(errorMessage(e2));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="New password" htmlFor="npw" hint="At least 6 characters">
          <Input id="npw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </Field>
        <Field label="Confirm password" htmlFor="npw2">
          <Input id="npw2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
        </Field>
        {err && <p className="text-[13px] text-red-600" role="alert">{err}</p>}
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Set password
        </Button>
      </form>
    </AuthCard>
  );
}

export function AcceptInvitePage() {
  const [token] = useState(fragToken);
  const nav = useNavigate();
  const qc = useQueryClient();
  const inv = useQuery({ queryKey: ['invite', token], queryFn: () => api(`/invitations/${encodeURIComponent(token)}`), enabled: !!token, retry: false });
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!token) return <AuthCard title="Invitation link missing"><Notice>Open the invitation link again.</Notice></AuthCard>;
  if (inv.isLoading) return <FullPageSpinner />;
  if (inv.isError) return <AuthCard title="Invitation unavailable"><ErrorState error={inv.error} /></AuthCard>;
  return (
    <AuthCard title="Set up your account" subtitle={<>You were invited as <strong>{inv.data.email}</strong>.</>}>
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            const r = await api('/invitations/' + encodeURIComponent(token) + '/accept', { body: { name, password: pw } });
            history.replaceState(null, '', location.pathname);
            await qc.invalidateQueries({ queryKey: ['me'] });
            nav(r.user.globalRole === 'client' ? '/portal' : '/admin', { replace: true });
          } catch (e2) {
            setErr(errorMessage(e2));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Your name" htmlFor="iname">
          <Input id="iname" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </Field>
        <Field label="Password" htmlFor="ipw" hint="At least 6 characters">
          <Input id="ipw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </Field>
        {err && <p className="text-[13px] text-red-600" role="alert">{err}</p>}
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Create account
        </Button>
      </form>
    </AuthCard>
  );
}
